import { type PropsWithChildren, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useViewport } from '@/hooks';
import { COACHMARK_BOX_OFFSET, COACHMARK_PLACEMENT } from './buildProFeatureCoachmark.config';
import { BuildProFeatureCoachmarkContent } from './BuildProFeatureCoachmarkContent';
import { BuildProFeatureCoachmarkContext } from './BuildProFeatureCoachmarkProvider';
import { type CoachmarkAlign, type CoachmarkSide, CoachmarkTooltip } from './CoachmarkTooltip';
import { type BuildProFeatureCoachmarkFeature } from './types';
import { useCoachmarkAnchorReady } from './useCoachmarkAnchorReady';
type CoachmarkPlacement = {
  side: CoachmarkSide;
  align: CoachmarkAlign;
};
const COACHMARK_BUBBLE_WIDTH = 275;
const MIN_VIEWPORT_RIGHT_GAP = 20;
const COACHMARK_SCROLL_DELAY_MS = 50;
export const BuildProFeatureCoachmarkAnchor = ({
  feature,
  children
}: PropsWithChildren<{
  feature: BuildProFeatureCoachmarkFeature;
}>) => {
  const router = useRouter();
  const triggerRef = useRef<HTMLDivElement>(null);
  const context = useContext(BuildProFeatureCoachmarkContext);
  const register = context?.register;
  const unregister = context?.unregister;
  const consumeInitialOpenAnimation = context?.consumeInitialOpenAnimation;
  const {
    isMobile,
    isPortable
  } = useViewport();
  const activeFeature = context?.activeFeature ?? null;
  const activeStep = context?.activeStep ?? 0;
  const totalSteps = context?.totalSteps ?? 0;
  const shouldAnimateInitialOpen = context?.shouldAnimateInitialOpen ?? false;
  const basePlacement = COACHMARK_PLACEMENT[feature][isPortable ? 'tablet' : 'pc'];
  const [placement, setPlacement] = useState<CoachmarkPlacement>(basePlacement);
  const [animateCurrentOpen, setAnimateCurrentOpen] = useState(false);
  const isOpen = !!context && activeFeature === feature && !isMobile;
  const shouldWaitForAnchorReady = activeStep === 1 && shouldAnimateInitialOpen;
  const isAnchorReady = useCoachmarkAnchorReady(isOpen && shouldWaitForAnchorReady, triggerRef);
  const shouldOpenTooltip = isOpen && (!shouldWaitForAnchorReady || isAnchorReady);
  const animateOnOpen = shouldAnimateInitialOpen || animateCurrentOpen;
  const hasExplicitFocusTarget = Boolean(router.query.targetField);
  useEffect(() => {
    if (!register || !unregister) return;
    register(feature, triggerRef.current);
    return () => unregister(feature);
  }, [feature, register, unregister]);
  useEffect(() => {
    if (!shouldOpenTooltip) {
      setAnimateCurrentOpen(false);
      return;
    }
    if (!shouldAnimateInitialOpen) return;
    setAnimateCurrentOpen(true);
    consumeInitialOpenAnimation?.();
  }, [consumeInitialOpenAnimation, shouldAnimateInitialOpen, shouldOpenTooltip]);
  useEffect(() => {
    if (!isOpen || hasExplicitFocusTarget) return;
    let timeoutId: number | undefined;
    let secondFrameId: number | undefined;
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          triggerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }, COACHMARK_SCROLL_DELAY_MS);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrameId);
      if (secondFrameId != null && secondFrameId !== 0) window.cancelAnimationFrame(secondFrameId);
      if (timeoutId != null && timeoutId !== 0) window.clearTimeout(timeoutId);
    };
  }, [activeStep, hasExplicitFocusTarget, isOpen]);
  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(basePlacement);
      return;
    }
    const updatePlacement = () => {
      const nextPlacement = resolveCoachmarkPlacement({
        basePlacement,
        feature,
        anchorElement: triggerRef.current
      });
      setPlacement(current => current.side === nextPlacement.side && current.align === nextPlacement.align ? current : nextPlacement);
    };
    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.visualViewport?.addEventListener('resize', updatePlacement);
    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.visualViewport?.removeEventListener('resize', updatePlacement);
    };
  }, [basePlacement, feature, isOpen]);
  if (!context) return <>{children}</>;
  return <CoachmarkTooltip open={shouldOpenTooltip} side={placement.side} align={placement.align} coachBoxOffset={COACHMARK_BOX_OFFSET} animateOnOpen={animateOnOpen} content={<BuildProFeatureCoachmarkContent feature={feature} activeStep={activeStep} totalSteps={totalSteps} />} data-sentry-element="CoachmarkTooltip" data-sentry-component="BuildProFeatureCoachmarkAnchor" data-sentry-source-file="BuildProFeatureCoachmarkAnchor.tsx">
      <div ref={triggerRef} className="inline-flex">
        {children}
      </div>
    </CoachmarkTooltip>;
};
const resolveCoachmarkPlacement = ({
  basePlacement,
  feature,
  anchorElement
}: {
  basePlacement: CoachmarkPlacement;
  feature: BuildProFeatureCoachmarkFeature;
  anchorElement: HTMLElement | null;
}): CoachmarkPlacement => {
  if (basePlacement.side !== 'right' || !anchorElement) return basePlacement;
  const anchorRect = anchorElement.getBoundingClientRect();
  const rightEdge = anchorRect.right + COACHMARK_BOX_OFFSET.leftRight + COACHMARK_BUBBLE_WIDTH;
  const rightGap = getViewportRight() - rightEdge;
  if (rightGap > MIN_VIEWPORT_RIGHT_GAP) return basePlacement;
  return COACHMARK_PLACEMENT[feature].tablet;
};
const getViewportRight = () => {
  if (typeof window === 'undefined') return 0;
  if (window.visualViewport) {
    return window.visualViewport.offsetLeft + window.visualViewport.width;
  }
  return window.innerWidth;
};